/**
 * シフト表(月間。2026-09-12)。現行「【日報2609】コールセンター」のシフト表タブと同じ見た目。
 *  - 縦 = スタッフ / 横 = 日付。セルは "9-18" のような時間、入らない日は空
 *  - **提出されたものを確定とする**(2026-09-22 発注者回答。P-45 → D-130)。直すときは管理者がそのセルを直し、
 *    **時間を変えたら本人にお知らせが出る**(欠勤・当欠の記録だけなら出さない)。本人が確認するまでセルの吹き出しに「本人未確認」
 *  - **提出から変わったセルは変更の種類で色を分ける**(2026-09-24 発注者指示。D-133):
 *    稼働追加=緑＋ / 稼働時間延長=青▲ / 稼働時間短縮=黄▼ / 休みに変更=赤×。欠勤・当欠と「時間帯だけの変更」はこれまでどおり印だけ
 *  - 「差」= 確定(いまのセル)− 提出(直す前)の時間。「報酬額」と「欠勤」の間(2026-09-15 MTG)
 *  - 原価率は、管理者が入れた条件(「◯%以上は青」など)で色を付ける(2026-09-15 MTG)
 *  - 上に 稼働合計 と 想定件数(稼働 × 2.0件/h)、絞り込みは 全体 / 品川 / 在宅
 *  - **管理(出勤形態=管理)は一番上に固定**
 * 日付の見出しをクリックするとその日の出勤表へ移動する。
 * readOnly(デザイン比較の画面で使う。D-132): 管理者の表示(全員の行・原価率など)のまま、セルの変更と原価率の色の設定を出さない(書き込まない)。
 * 2026-09-20 UIデザインシステムへ移行(D-99): 枠は Panel、月移動は FilterBar、絞り込みは SegmentedControl、
 * 表は `.report-scroll` ＋ `.report-table`(名前の列を左に固定)、数字は formatters。
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayColorOf } from '@core/lib/holidays';
import type { DateStr, MonthStr, Operator, WorkStyle } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperatorById, fetchOperators } from '@core/masters/operatorRepo';
import { apphOf, costRate, staffMetrics, sumAgg } from '@core/dashboard/calc';
import { fetchMonthTargets, fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import { addMonths, daysInMonth } from '@core/dashboard/calc';
import { TARGET_PER_HOUR, TARGET_PER_HOUR_BY_BUSINESS, businessOfBbId } from './shiftRepo';
import Modal from '@core/components/Modal';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import FilterBar from '@core/components/ui/FilterBar';
import Panel from '@core/components/ui/Panel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import { formatCount, formatCurrency, formatDecimal, formatPercent } from '@core/utils/formatters';
import { breakMinOf, dayHours, fetchSubmission, fetchSubmissions, gapHoursOf, gapLabel, withTimes, type ShiftChangeLog, type ShiftSubmission, type SubmitDay } from './shiftSubmitRepo';
import { fetchMonthNotices, saveSubmissionByAdmin, styleResolver, type ShiftNoticeItem } from './shiftNoticeRepo';
import {
  RULE_COLOR_LABELS,
  RULE_COLOR_STYLES,
  RULE_OP_LABELS,
  SHIFT_CHANGE_KINDS,
  SHIFT_CHANGE_LABELS,
  SHIFT_CHANGE_MARKS,
  cellText,
  changeKindOf,
  colorOfCostRate,
  fetchCostRateRules,
  firstChangeByDay,
  fmtDiff,
  saveCostRateRules,
  shiftDiffHours,
  submittedHoursOf,
  type CostRateRule,
  type RuleColor,
  type RuleOp,
} from './shiftDisplay';

type Scope = 'all' | 'shinagawa' | 'zaitaku';
const SCOPES: { key: Scope; label: string }[] = [
  { key: 'all', label: '全体' },
  { key: 'shinagawa', label: '出勤' },
  { key: 'zaitaku', label: '在宅' },
];
/** 出勤形態×報酬区分のまとまり(現行シートの並び)。管理が先頭 */
type GroupKey = 'kanri' | 'shinagawa' | 'timee' | 'zaitaku_jikyu' | 'zaitaku_buai';
const GROUPS: { key: GroupKey; label: string; cls: string; sums: ('hours' | 'ex_timee' | 'people')[] }[] = [
  { key: 'kanri', label: '管理', cls: 'g-kanri', sums: ['hours'] },
  { key: 'shinagawa', label: '出勤', cls: 'g-shinagawa', sums: ['hours', 'ex_timee'] },
  { key: 'timee', label: 'タイミー', cls: 'g-timee', sums: ['hours', 'people'] },
  { key: 'zaitaku_jikyu', label: '在宅／時給', cls: 'g-jikyu', sums: ['hours', 'people'] },
  { key: 'zaitaku_buai', label: '在宅／歩合', cls: 'g-buai', sums: ['hours', 'people'] },
];
const SUM_LABELS = { hours: '稼働時間合計', ex_timee: 'タイミー抜き', people: '稼働人数合計' } as const;
/** 時間の表示(15分単位。D-127): 8 → 8、7.25 → 7.25、6.5 → 6.5 */
const fmtH = (h: number) => String(Math.round(h * 100) / 100);
const hours = Array.from({ length: 23 }, (_, i) => 8 + i * 0.5);
const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;

export default function ShiftMonthTab({ onOpenDate, readOnly = false }: { onOpenDate: (d: DateStr) => void; readOnly?: boolean }) {
  const { user, isAdmin } = useAuth();
  /** セルの変更・原価率の色の設定ができるか。readOnly のときは管理者でも出さない(書き込みはすべてこの判定を通る) */
  const canEdit = isAdmin && !readOnly;
  const toast = useToast();
  const [month, setMonth] = useState<MonthStr>(todayStr().slice(0, 7) as MonthStr);
  const [scope, setScope] = useState<Scope>('all');
  const [subs, setSubs] = useState<ShiftSubmission[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  /** op_id → 今月の実績(原価率・取得成績)。日次【集計】と同じ計算 */
  const [perf, setPerf] = useState<Map<string, { costRate: number | null; apph: number | null }>>(new Map());
  const [loading, setLoading] = useState(true);
  /** 原価率の条件付き書式(管理者が設定) */
  const [rules, setRules] = useState<CostRateRule[]>([]);
  const [ruleDraft, setRuleDraft] = useState<{ op: RuleOp; value: string; color: RuleColor }[] | null>(null);
  /** op_id → 本人がまだ確認していない変更(日 → お知らせ)。管理者だけ(D-130) */
  const [unread, setUnread] = useState<Map<string, Record<string, ShiftNoticeItem>>>(new Map());

  const loadUnread = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const ns = await fetchMonthNotices(month);
      setUnread(new Map(ns.map((n) => [n.op_id, n.items])));
    } catch {
      setUnread(new Map());
    }
  }, [month, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!isAdmin) {
        // 本人(operator)は自分の提出と自分のオペレーター情報だけ読める(全員分・日次集計・会社は firestore.rules で読めない)
        const opId = user?.op_id ?? '';
        const [mine, me] = await Promise.all([opId ? fetchSubmission(month, opId) : Promise.resolve(null), opId ? fetchOperatorById(opId) : Promise.resolve(null)]);
        setSubs(mine ? [mine] : []);
        setOperators(me ? [me] : []);
        setPerf(new Map());
        return;
      }
      const from = `${month}-01` as DateStr;
      const to = `${month}-${String(daysInMonth(month)).padStart(2, '0')}` as DateStr;
      const [s, ops, sums, tg, cs, rs] = await Promise.all([
        fetchSubmissions(month),
        isAdmin ? fetchOperators() : Promise.resolve([] as Operator[]),
        fetchSummariesRange(from, to),
        fetchMonthTargets(month),
        fetchCompanies(),
        fetchCostRateRules().catch(() => [] as CostRateRule[]),
      ]);
      void loadUnread();
      setSubs(s);
      setRules(rs);
      setOperators(ops);
      const cmap = new Map(cs.map((c) => [c.id, c]));
      const metrics = sums.flatMap((x) => x.staff.map((st) => staffMetrics(st, x, tg, cmap)));
      const m = new Map<string, { costRate: number | null; apph: number | null }>();
      for (const opId of new Set(metrics.map((x) => x.op_id))) {
        const agg = sumAgg(metrics.filter((x) => x.op_id === opId));
        m.set(opId, { costRate: costRate(agg), apph: apphOf(agg) });
      }
      setPerf(m);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, isAdmin, user?.op_id, toast, loadUnread]);

  useEffect(() => {
    void load();
  }, [load]);

  const days = useMemo(() => Array.from({ length: daysInMonth(month) }, (_, i) => String(i + 1).padStart(2, '0')), [month]);
  /** 日曜日の列は網掛け(2026-09-15 MTG) */
  const sundays = useMemo(
    () => new Set(days.filter((d) => new Date(`${month}-${d}T00:00:00Z`).getUTCDay() === 0)),
    [days, month],
  );
  const hatch = (d: string) => (sundays.has(d) ? ' hatch' : '');
  const opOf = useCallback((opId: string) => operators.find((o) => o.id === opId) ?? null, [operators]);
  const wsOf = useCallback((opId: string): WorkStyle => opOf(opId)?.work_style ?? 'zaitaku', [opOf]);
  /** その人のまとまり(出勤形態×報酬区分) */
  const groupOf = useCallback(
    (opId: string): GroupKey => {
      const op = opOf(opId);
      const ws = op?.work_style ?? 'zaitaku';
      if (ws === 'kanri') return 'kanri';
      if (ws === 'shinagawa') return 'shinagawa';
      if (ws === 'timee') return 'timee';
      return op?.pay_type === 'commission' ? 'zaitaku_buai' : 'zaitaku_jikyu';
    },
    [opOf],
  );
  /** 報酬(時給 or 件数単価)。先頭のBB IDの現在値 */
  const wageOf = useCallback((opId: string) => opOf(opId)?.bb_accounts[0]?.current_wage ?? null, [opOf]);

  const rows = useMemo(() => {
    const list = subs
      .filter((s) => (isAdmin ? true : s.op_id === user?.op_id))
      .filter((s) => {
        if (scope === 'all') return true;
        const ws = wsOf(s.op_id);
        if (ws === 'kanri') return true; // 管理は常に出す
        return scope === 'shinagawa' ? ws === 'shinagawa' || ws === 'timee' : ws === 'zaitaku';
      });
    return [...list].sort((a, b) => {
      const d = GROUPS.findIndex((g) => g.key === groupOf(a.op_id)) - GROUPS.findIndex((g) => g.key === groupOf(b.op_id));
      return d !== 0 ? d : a.name.localeCompare(b.name, 'ja');
    });
  }, [subs, isAdmin, user?.op_id, scope, wsOf, groupOf]);

  /** まとまりの開閉(全体表示のとき、出勤形態×報酬で表示/非表示できる) */
  const [closed, setClosed] = useState<Set<GroupKey>>(new Set());
  const toggleGroup = (k: GroupKey) =>
    setClosed((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const hoursOf = (s: ShiftSubmission, d: string) => dayHours(s.days[d]);
  const countStatus = (s: ShiftSubmission, st: 'absent' | 'same_day') => days.filter((d) => s.days[d]?.status === st).length;
  const dayTotal = (d: string) => rows.reduce((a, s) => a + hoursOf(s, d), 0);
  const rowTotal = (s: ShiftSubmission) => days.reduce((a, d) => a + hoursOf(s, d), 0);
  const grand = rows.reduce((a, s) => a + rowTotal(s), 0);
  /** 想定件数 = 稼働 × 想定取得成績。その日のBB IDの架電内容で係数を変える(給湯器 2.0件/h・不用品 3時間1件) */
  const dayTarget = (d: string) =>
    rows.reduce((a, s) => a + hoursOf(s, d) * TARGET_PER_HOUR_BY_BUSINESS[businessOfBbId(operators, s.days[d]?.bb_id || s.bb_id)], 0);
  const grandTarget = days.reduce((a, d) => a + dayTarget(d), 0);

  /** 編集中のセル */
  const [edit, setEdit] = useState<{ s: ShiftSubmission; day: string; start: number | null; end: number | null } | null>(null);
  const [saving, setSaving] = useState(false);

  const openCell = (s: ShiftSubmission, day: string) => {
    if (!canEdit) return;
    const v = s.days[day];
    setEdit({ s, day, start: v?.start ?? 9, end: v?.end ?? 18 });
  };

  const applyCell = async (remove: boolean, status: '' | 'absent' | 'same_day') => {
    if (!edit || !user || !canEdit) return;
    const { s, day } = edit;
    const before = cellText(s.days[day]) || '休み';
    const next = { ...s.days };
    if (remove) delete next[day];
    else {
      if (edit.start === null || edit.end === null || edit.end <= edit.start) {
        toast('終了は開始より後にしてください');
        return;
      }
      // 休憩・中抜け・備考(D-126)は残し、欠勤などの状態だけ付け直す。休憩が既定値のままなら新しい時間の既定値に(withTimes)
      const { status: _old, ...rest } = withTimes(s.days[day], edit.start, edit.end);
      const d: SubmitDay = { ...rest, bb_id: s.days[day]?.bb_id ?? s.bb_id };
      if (status) d.status = status;
      next[day] = d;
    }
    const after = cellText(next[day]) || '休み';
    if (before === after) {
      setEdit(null);
      return;
    }
    const by = user.name || user.email || user.id;
    const log: ShiftChangeLog = { day, before, after, by, at: new Date().toISOString(), before_hours: dayHours(s.days[day]) };
    const logs = [log, ...(s.logs ?? [])];
    setSaving(true);
    try {
      // シフト提出とお知らせを1回で書く。自分のシフトを直したときは知らせない(D-130)
      const noticeDays = await saveSubmissionByAdmin({
        month,
        opId: s.op_id,
        name: s.name,
        bbId: s.bb_id,
        days: next,
        note: s.note ?? '',
        userId: user.id,
        logs,
        prevDays: s.days,
        byName: by,
        silent: !!user.op_id && user.op_id === s.op_id,
        styleOf: styleResolver(operators),
      });
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, days: next, logs } : x)));
      setEdit(null);
      if (noticeDays.includes(day)) toast(`保存しました。${s.name} さんにお知らせを出しました`);
      void loadUnread();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  async function saveRules() {
    if (!ruleDraft || !user || !canEdit) return;
    const next: CostRateRule[] = [];
    for (const r of ruleDraft) {
      const v = Number(r.value.trim().replace(/[％%]$/, ''));
      if (r.value.trim() === '' || !Number.isFinite(v) || v < 0) {
        toast('原価率の値は0以上の数字で入れてください');
        return;
      }
      next.push({ op: r.op, value: v, color: r.color });
    }
    setSaving(true);
    try {
      await saveCostRateRules(next, user.id);
      setRules(next);
      setRuleDraft(null);
      toast('原価率の色を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }

  /** op_id → 日 → その日の最初の変更(提出したときの姿)。セルの色分けと吹き出しに使う */
  const firstChanges = useMemo(() => new Map(rows.map((s) => [s.id, firstChangeByDay(s.logs)])), [rows]);
  /** 変更のある人が1人でもいるときだけ凡例を出す */
  const hasChanges = useMemo(() => rows.some((s) => (s.logs ?? []).length > 0), [rows]);
  /** 本人がまだ確認していない変更の件数(表示中の人) */
  const unreadCount = rows.reduce((a, s) => a + Object.keys(unread.get(s.op_id) ?? {}).length, 0);

  const monthLabel = `${month.slice(0, 4)}年${Number(month.slice(5))}月`;
  /** 名前〜当欠 の列数。原価率・取得成績・報酬額は管理者だけ(本人には見せない) */
  const leftCols = isAdmin ? 8 : 5;
  return (
    <>
      <FilterBar label="表示する月">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1) as MonthStr)} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value as MonthStr)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1) as MonthStr)} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">{monthLabel}</span>
        <SegmentedControl label="絞り込み" options={SCOPES.map((x) => ({ key: x.key, label: x.label }))} value={scope} onChange={(v) => setScope(v as Scope)} />
      </FilterBar>

      <Panel
        title={`シフト表(${monthLabel})`}
        meta={
          rows.length > 0
            ? `${formatCount(rows.length, { unit: '名' })}${unreadCount > 0 ? ` ／ 本人未確認の変更 ${formatCount(unreadCount, { unit: '件' })}` : ''}`
            : undefined
        }
        actions={
          canEdit ? (
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => setRuleDraft(rules.map((r) => ({ op: r.op, value: String(r.value), color: r.color })))}>
              原価率の色{rules.length > 0 ? `(${rules.length})` : ''}
            </button>
          ) : undefined
        }
        note={
          canEdit
            ? 'セルをクリックすると直せます(例 9-18／休みは空欄)。**時間を直すと本人にお知らせが出ます**(欠勤・当欠は出ません)。日付の見出しを押すとその日の出勤表へ移動します'
            : '日付の見出しを押すとその日の出勤表へ移動します'
        }
      >
      {loading ? (
        <div className="ui-empty">読み込み中…</div>
      ) : rows.length === 0 ? (
        <div className="ui-empty">この月の提出がまだありません</div>
      ) : (
        <>
        {hasChanges && (
          <div className="shift-legend">
            <span>提出からの変更</span>
            {SHIFT_CHANGE_KINDS.map((k) => (
              <span key={k} className={`shift-legend-item chg-${k}`}>
                <span className="chg-mark" aria-hidden="true">
                  {SHIFT_CHANGE_MARKS[k]}
                </span>
                {SHIFT_CHANGE_LABELS[k]}
              </span>
            ))}
            <span className="ui-hint">欠勤・当欠と、時間帯だけの変更は印のみ</span>
          </div>
        )}
        <div className="report-scroll is-matrix">
          <table className="report-table is-wide no-stack shift-month">
            <thead>
              <tr>
                <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 0, width: 116, minWidth: 116 }}>名前</th>
                <th scope="col" className="is-center" style={{ width: 52 }}>形態</th>
                {isAdmin && (
                  <>
                    <th scope="col" className="is-num" style={{ width: 60 }}>原価率</th>
                    <th scope="col" className="is-num" style={{ width: 60 }}>取得成績</th>
                    <th scope="col" className="is-num" style={{ width: 70 }}>報酬額</th>
                  </>
                )}
                <th scope="col" className="is-num" style={{ width: 48 }} title="確定シフト − 提出シフト(時間)">差</th>
                <th scope="col" className="is-num" style={{ width: 44 }}>欠勤</th>
                <th scope="col" className="is-num" style={{ width: 44 }}>当欠</th>
                {days.map((d) => {
                  const date = `${month}-${d}` as DateStr;
                  const c = dayColorOf(date);
                  return (
                    <th key={d} scope="col" className={`is-num day ${c === 'red' ? 'is-sunday' : c === 'blue' ? 'is-holiday' : ''}${hatch(d)}`}>
                      <button type="button" className="report-link" onClick={() => onOpenDate(date)}>
                        {Number(d)} {weekdayLabel(date).replace(/[()]/g, '')}
                      </button>
                    </th>
                  );
                })}
                <th scope="col" className="is-num" style={{ width: 46 }}>日数</th>
                <th scope="col" className="is-num col-total is-sticky-right" style={{ width: 62 }}>合計</th>
              </tr>
              <tr className="shift-sum">
                <th colSpan={leftCols}>稼働合計</th>
                {days.map((d) => (
                  <th key={d} className={`is-num day${hatch(d)}`}>{dayTotal(d) ? fmtH(dayTotal(d)) : ''}</th>
                ))}
                <th className="is-num">{formatCount(rows.filter((s) => rowTotal(s) > 0).length)}</th>
                <th className="is-num col-total is-sticky-right">{fmtH(grand)}</th>
              </tr>
              <tr className="shift-sum">
                <th colSpan={leftCols}>想定件数(×{TARGET_PER_HOUR}・不用品は3時間1件)</th>
                {days.map((d) => (
                  <th key={d} className={`is-num day${hatch(d)}`}>{dayTotal(d) ? Math.round(dayTarget(d)) : ''}</th>
                ))}
                <th />
                <th className="is-num col-total is-sticky-right">{formatCount(Math.round(grandTarget))}</th>
              </tr>
            </thead>
            <tbody>
              {GROUPS.map((g) => {
                const list = rows.filter((s) => groupOf(s.op_id) === g.key);
                if (list.length === 0) return null;
                const open = !closed.has(g.key);
                const gHours = (d: string) => list.reduce((a, s) => a + hoursOf(s, d), 0);
                const gPeople = (d: string) => list.filter((s) => hoursOf(s, d) > 0).length;
                const exTimee = (d: string) => list.filter((s) => wsOf(s.op_id) !== 'timee').reduce((a, s) => a + hoursOf(s, d), 0);
                return (
                  <Fragment key={g.key}>
                    <tr className={`shift-grp ${g.cls}`}>
                      <th colSpan={leftCols}>
                        <button className="grp-toggle" onClick={() => toggleGroup(g.key)}>
                          {open ? '−' : '＋'} {g.label}({list.length}名)
                        </button>
                      </th>
                      {days.map((d) => (
                        <th key={d} className={`is-num day${hatch(d)}`} />
                      ))}
                      <th />
                      <th />
                    </tr>
                    {isAdmin && g.sums.map((k) => (
                      <tr key={k} className="shift-gsum">
                        <th colSpan={leftCols}>{SUM_LABELS[k]}</th>
                        {days.map((d) => {
                          const v = k === 'hours' ? gHours(d) : k === 'people' ? gPeople(d) : k === 'ex_timee' ? exTimee(d) : null;
                          return (
                            <td key={d} className={`is-num day${hatch(d)}`}>
                              {v ? (k === 'people' ? formatCount(v) : formatDecimal(v, { digits: 1 })) : ''}
                            </td>
                          );
                        })}
                        <td />
                        <td className="is-num col-total is-sticky-right">
                          {k === 'hours' ? fmtH(list.reduce((a, s) => a + rowTotal(s), 0)) : k === 'people' ? formatCount(list.filter((s) => rowTotal(s) > 0).length) : ''}
                        </td>
                      </tr>
                    ))}
                    {open &&
                      list.map((s) => (
                        <tr key={s.id} className={g.key === 'kanri' ? 'is-kanri' : undefined}>
                          <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 0 }}>
                            {s.name}
                          </th>
                          <td className="is-center ws-cell">
                            <span className={`ws-tag ${g.cls}`}>{g.label}</span>
                          </td>
                          {isAdmin && (
                            <>
                              {(() => {
                                const rate = perf.get(s.op_id)?.costRate ?? null;
                                const color = colorOfCostRate(rate, rules);
                                return (
                                  <td className="is-num" style={color ? { ...RULE_COLOR_STYLES[color], fontWeight: 700 } : undefined}>
                                    {formatPercent(rate)}
                                  </td>
                                );
                              })()}
                              <td className="is-num">{formatDecimal(perf.get(s.op_id)?.apph ?? null, { digits: 2 })}</td>
                              <td className="is-num">{wageOf(s.op_id) === null ? '' : formatCurrency(wageOf(s.op_id))}</td>
                            </>
                          )}
                          <td className={`is-num${shiftDiffHours(s) < 0 ? ' neg' : ''}`} title={`提出 ${fmtH(submittedHoursOf(s))}h → 確定 ${fmtH(rowTotal(s))}h`}>
                            {fmtDiff(shiftDiffHours(s))}
                          </td>
                          <td className="is-num">{countStatus(s, 'absent') || ''}</td>
                          <td className="is-num">{countStatus(s, 'same_day') || ''}</td>
                          {days.map((d) => {
                            const v = s.days[d];
                            const first = firstChanges.get(s.id)?.get(d);
                            // 提出からどう変わったか(稼働追加・延長・短縮・休みに変更)。欠勤・時間帯だけの変更は印だけ
                            const kind = first ? changeKindOf(first, v) : null;
                            const notRead = !!unread.get(s.op_id)?.[d];
                            return (
                              <td
                                key={d}
                                className={`day${v ? ' has' : ''}${v?.status ? ' off' : ''}${first ? ' changed' : ''}${kind && kind !== 'other' ? ` chg-${kind}` : ''}${canEdit ? ' clickable' : ''}${hatch(d)}`}
                                title={
                                  [
                                    first
                                      ? `${kind && kind !== 'other' ? `${SHIFT_CHANGE_LABELS[kind]}: ` : ''}${(s.logs ?? [])
                                          .filter((l) => l.day === d)
                                          .map((l) => `${l.before} → ${l.after}(${l.by})`)
                                          .join(' / ')}${notRead ? ' ・本人未確認' : ''}`
                                      : '',
                                    // 提出の休憩・中抜け(2026-09-25 発注者指示)
                                    v && !v.status ? `休憩${breakMinOf(v)}分${gapHoursOf(v) ? `・中抜け${gapLabel(v)}` : ''}${v.note ? `(${v.note})` : ''}` : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ／ ') || undefined
                                }
                                onClick={canEdit ? () => openCell(s, d) : undefined}
                              >
                                {cellText(v)}
                                {/* 中抜けのある日は印を出す(2026-09-25 発注者指示) */}
                                {v && gapHoursOf(v) > 0 ? <span className="gap-mark" aria-label={`中抜け ${gapLabel(v)}`}>中</span> : null}
                                {kind && kind !== 'other' && (
                                  <span className="chg-mark" aria-label={SHIFT_CHANGE_LABELS[kind]}>
                                    {SHIFT_CHANGE_MARKS[kind]}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          <td className="is-num">{formatCount(days.filter((d) => hoursOf(s, d) > 0).length)}</td>
                          <td className="is-num col-total is-sticky-right">
                            <b>{fmtH(rowTotal(s))}</b>
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      </Panel>

      {ruleDraft && canEdit && (
        <Modal onClose={() => setRuleDraft(null)} width="min(520px, 94vw)">
          <h3>原価率の色</h3>
          <p className="hint" style={{ margin: '6px 0 10px' }}>上の条件から順に見て、最初に当てはまった色を付けます。</p>
          <table className="no-stack" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>原価率</th>
                <th>条件</th>
                <th>色</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {ruleDraft.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input value={r.value} inputMode="decimal" onChange={(e) => setRuleDraft(ruleDraft.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} style={{ width: 80, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit' }} />
                    <span style={{ marginLeft: 4 }}>%</span>
                  </td>
                  <td>
                    <select value={r.op} onChange={(e) => setRuleDraft(ruleDraft.map((x, j) => (j === i ? { ...x, op: e.target.value as RuleOp } : x)))}>
                      {(Object.keys(RULE_OP_LABELS) as RuleOp[]).map((k) => (
                        <option key={k} value={k}>
                          {RULE_OP_LABELS[k]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select value={r.color} onChange={(e) => setRuleDraft(ruleDraft.map((x, j) => (j === i ? { ...x, color: e.target.value as RuleColor } : x)))} style={RULE_COLOR_STYLES[r.color]}>
                      {(Object.keys(RULE_COLOR_LABELS) as RuleColor[]).map((k) => (
                        <option key={k} value={k}>
                          {RULE_COLOR_LABELS[k]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button className="txn-link" onClick={() => setRuleDraft(ruleDraft.filter((_, j) => j !== i))}>
                      −
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="txn-link" style={{ marginTop: 6 }} onClick={() => setRuleDraft([...ruleDraft, { op: 'gte', value: '', color: 'blue' }])}>
            ＋条件を追加
          </button>
          <div className="modal-actions">
            <button className="btn-green" disabled={saving} onClick={() => void saveRules()}>
              保存する
            </button>
            <button className="btn-line" disabled={saving} onClick={() => setRuleDraft(null)}>
              閉じる
            </button>
          </div>
        </Modal>
      )}

      {edit && canEdit && (
        <Modal onClose={() => setEdit(null)} width="min(420px, 94vw)">
          <h3>
            {edit.s.name}・{Number(edit.day)}日{weekdayLabel(`${month}-${edit.day}` as DateStr)}
          </h3>
          <div className="shift-edit">
            <label>開始</label>
            <select value={edit.start ?? ''} onChange={(e) => setEdit({ ...edit, start: Number(e.target.value) })}>
              {hours.map((h) => (
                <option key={h} value={h}>
                  {hhmm(h)}
                </option>
              ))}
            </select>
            <label>終了</label>
            <select value={edit.end ?? ''} onChange={(e) => setEdit({ ...edit, end: Number(e.target.value) })}>
              {hours.map((h) => (
                <option key={h} value={h}>
                  {hhmm(h)}
                </option>
              ))}
            </select>
          </div>
          {/* 本人が出した休憩・中抜け(2026-09-28 発注者指示「詳細を開いた際にも表示」)。ここでは直せない(シフト提出の値) */}
          {/* 本人が出した休憩・中抜け。**行を分ける**(2026-09-28 発注者指示「同じで見づらい」)。ここでは直せない(シフト提出の値) */}
          <dl className="shift-edit-rest">
            <dt>休憩</dt>
            <dd>{edit.s.days[edit.day] ? `${breakMinOf(edit.s.days[edit.day]!)}分` : '—'}</dd>
            <dt>中抜け</dt>
            <dd>{edit.s.days[edit.day] && gapHoursOf(edit.s.days[edit.day]!) > 0 ? gapLabel(edit.s.days[edit.day]!) : 'なし'}</dd>
            {(edit.s.days[edit.day]?.note ?? '').trim() && (
              <>
                <dt>備考</dt>
                <dd>{edit.s.days[edit.day]!.note}</dd>
              </>
            )}
          </dl>
          {(edit.s.logs ?? []).filter((l) => l.day === edit.day).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="hint" style={{ marginBottom: 4 }}>この日の変更履歴</div>
              <ul style={{ fontSize: 12, paddingLeft: 16 }}>
                {(edit.s.logs ?? [])
                  .filter((l) => l.day === edit.day)
                  .slice(0, 5)
                  .map((l, i) => (
                    <li key={i}>
                      {l.before} → {l.after}({l.by}・{l.at.slice(0, 16).replace('T', ' ')})
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <div className="modal-actions center">
            <button className="btn-line" disabled={saving} onClick={() => void applyCell(false, 'absent')}>
              欠勤
            </button>
            <button className="btn-line" disabled={saving} onClick={() => void applyCell(false, 'same_day')}>
              当欠
            </button>
            <button className="btn" disabled={saving} onClick={() => void applyCell(false, '')}>
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
